import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Loader2, Check, MapPin, Shield, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PinKeypad } from '@/components/ui/pin-keypad';
import { PinDisplay } from '@/components/ui/pin-display';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type TeamRole = 'admin' | 'vendedor';

const storeLocations = [
  'Santa Ana',
  'San Pedro',
  'Cartago',
  'Heredia Pasel Las Flores',
  'Heredia Fátima',
  'Bodega',
  'Paseo Metrópoli',
  'Paseo las Flores',
  'Mall San Pedro',
  'Santa Ana Town Center',
  'Megasuper Heredia Centro',
  'Vendedor comodín',
];

const roleLabels: Record<TeamRole, { label: string; color: string; bgColor: string; borderColor: string }> = {
  admin: { label: 'Administrador', color: 'text-amber-400', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/30' },
  vendedor: { label: 'Vendedor', color: 'text-purple-400', bgColor: 'bg-purple-500/20', borderColor: 'border-purple-500/30' },
};

// Validate name has at least 2 words
const validateName = (name: string): { valid: boolean; error?: string } => {
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const words = cleanName.split(' ').filter(w => w.length > 0);
  if (words.length < 2) {
    return { valid: false, error: 'Por favor ingresa nombre y apellido (ej: María López)' };
  }
  return { valid: true };
};

interface CreateTeamMemberFormProps {
  onMemberCreated?: () => void;
}

export const CreateTeamMemberForm: React.FC<CreateTeamMemberFormProps> = ({ onMemberCreated }) => {
  const { toast } = useToast();
  const [memberName, setMemberName] = useState('');
  const [memberPin, setMemberPin] = useState('');
  const [memberLocation, setMemberLocation] = useState('');
  const [memberRole, setMemberRole] = useState<TeamRole>('vendedor');
  const [creating, setCreating] = useState(false);

  const handleCreateMember = async () => {
    // Validate name (nombre y apellido)
    const nameValidation = validateName(memberName);
    if (!nameValidation.valid) {
      toast({
        title: 'Error',
        description: nameValidation.error,
        variant: 'destructive',
      });
      return;
    }

    if (memberPin.length !== 6) {
      toast({
        title: 'Error',
        description: 'El PIN debe tener 6 dígitos',
        variant: 'destructive',
      });
      return;
    }

    // Location required only for sellers
    if (memberRole === 'vendedor' && !memberLocation) {
      toast({
        title: 'Error',
        description: 'Seleccioná la ubicación de la tienda',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No hay sesión activa');
      }

      const response = await supabase.functions.invoke('create-team-member', {
        body: { 
          nombre: memberName.trim(), 
          pin: memberPin,
          ubicacion_tienda: memberRole === 'vendedor' ? memberLocation : null,
          role: memberRole,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Error al crear usuario');
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      const roleLabel = roleLabels[memberRole].label;
      toast({
        title: `¡${roleLabel} creado!`,
        description: `${memberName.trim()} ya puede acceder al sistema`,
      });

      setMemberName('');
      setMemberPin('');
      setMemberLocation('');
      setMemberRole('vendedor');
      
      onMemberCreated?.();
    } catch (error: any) {
      console.error('Error creating team member:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear el usuario',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/10"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
          <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-base sm:text-lg">Crear Nuevo Usuario</h3>
          <p className="text-indigo-400/80 text-xs sm:text-sm">
            Agregar un administrador o vendedor al equipo
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Role Selection */}
        <div className="space-y-2">
          <Label className="text-white/80 text-sm">
            Rol del usuario
          </Label>
          <Select value={memberRole} onValueChange={(value: TeamRole) => setMemberRole(value)}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Seleccionar rol" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10 z-[200]">
              <SelectItem 
                value="admin" 
                className="text-white hover:bg-white/10 focus:bg-white/10"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <span>Administrador</span>
                </div>
              </SelectItem>
              <SelectItem 
                value="vendedor" 
                className="text-white hover:bg-white/10 focus:bg-white/10"
              >
                <div className="flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-purple-400" />
                  <span>Vendedor</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Name Input */}
        <div className="space-y-2">
          <Label htmlFor="member-name" className="text-white/80 text-sm">
            Nombre y Apellido
          </Label>
          <Input
            id="member-name"
            placeholder="Ej: María López"
            value={memberName}
            onChange={(e) => setMemberName(e.target.value)}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
          />
        </div>

        {/* Store Location - Only for sellers */}
        {memberRole === 'vendedor' && (
          <div className="space-y-2">
            <Label className="text-white/80 text-sm flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              Tienda que atiende
            </Label>
            <Select value={memberLocation} onValueChange={setMemberLocation}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Seleccionar ubicación" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10 z-[200]">
                {storeLocations.map((location) => (
                  <SelectItem 
                    key={location} 
                    value={location} 
                    className="text-white hover:bg-white/10 focus:bg-white/10"
                  >
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* PIN Input */}
        <div className="space-y-3">
          <Label className="text-white/80 text-sm">PIN de acceso (6 dígitos)</Label>
          <div className="flex justify-center mb-3">
            <PinDisplay value={memberPin} variant="blue" maxLength={6} compact />
          </div>
          <div className="flex justify-center">
            <PinKeypad
              onNumberClick={(num) => memberPin.length < 6 && setMemberPin(memberPin + num)}
              onDelete={() => setMemberPin(memberPin.slice(0, -1))}
              variant="blue"
              compact
            />
          </div>
        </div>

        {/* Create Button */}
        <div className="flex justify-center pt-2">
          <Button
            onClick={handleCreateMember}
            disabled={
              creating || 
              !memberName.trim() || 
              memberPin.length !== 6 || 
              (memberRole === 'vendedor' && !memberLocation)
            }
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 sm:px-8 w-full sm:w-auto"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Crear {roleLabels[memberRole].label}
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default CreateTeamMemberForm;
